import { useEffect, useRef } from 'react';
import {
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import { useContext } from 'react';
import { API_SERVICE } from '@/api/candidate-job-slider';
import { CandidateContext } from '@/components/candidate-job-slider-v1/context/index';
import { type ApplicationData } from '@/components/candidate-job-slider-v1/types/application.interface';
import {
  applicationNotesQueryKey,
  APPLICATION_NOTES_STALE_MS,
  APPLICATION_NOTES_QUERY_ROOT,
} from '@/utils/application-notes-query-key';
import { useShowToast } from '@/hooks';

const SUPPORT_ERROR_MESSAGE =
  'Something went wrong. Please contact support@kula.ai if this continues.';

/** Keep in sync with `useQuery` below (slider + list prefetch). */
export const APPLICATION_QUERY_STALE_MS = 5 * 60 * 1000;

const isNullish = (value: unknown): value is null | undefined =>
  value === null || value === undefined;

export function getApplicationQueryKey(
  applicationId: string | null | undefined
): [string] {
  if (
    isNullish(applicationId) ||
    applicationId === '' ||
    String(applicationId) === 'null'
  ) {
    return ['application/__none__'];
  }
  return [`application/${applicationId}`];
}

/** Warm cache for the job-details application drawer (`include[]=stage_actions`). */
export function prefetchApplication(
  queryClient: QueryClient,
  applicationId: string | number | null | undefined
): Promise<void> {
  const id =
    isNullish(applicationId) || applicationId === ''
      ? null
      : String(applicationId);
  if (!id || id === 'null') {
    return Promise.resolve();
  }
  const queryKey = getApplicationQueryKey(id);
  return queryClient.prefetchQuery({
    queryKey,
    queryFn: () =>
      API_SERVICE.getApplication(id, {
        include: ['stage_actions'],
      }),
    staleTime: APPLICATION_QUERY_STALE_MS,
  });
}

/** Warm the first page of notes so the notes panel is instant on navigation. */
export function prefetchApplicationNotes(
  queryClient: QueryClient,
  applicationId: string | number | null | undefined
): Promise<void> {
  const id =
    isNullish(applicationId) || applicationId === ''
      ? null
      : String(applicationId);
  if (!id || id === 'null') {
    return Promise.resolve();
  }
  return queryClient.prefetchQuery({
    queryKey: applicationNotesQueryKey(id, 1),
    queryFn: () => API_SERVICE.getApplicationNotes(id, { page: 1 }),
    staleTime: APPLICATION_NOTES_STALE_MS,
  });
}

/**
 * Compute the ids of the applications immediately surrounding `currentAppId`
 * within `applications`, up to `radius` on each side. Excludes the current app
 * and any nullish ids. Returns [] when the current app isn't in the list.
 *
 * Used to warm prev/next application payloads so ‹ › navigation is instant.
 */
export function getAdjacentApplicationIds(
  applications: Array<{ appId?: string | number | null }> | undefined,
  currentAppId: string | number | null | undefined,
  radius = 1
): string[] {
  if (!applications?.length || isNullish(currentAppId)) return [];
  const current = String(currentAppId);
  const currentIndex = applications.findIndex(
    (app) => !isNullish(app?.appId) && String(app.appId) === current
  );
  if (currentIndex === -1) return [];

  const ids: string[] = [];
  for (let offset = 1; offset <= radius; offset += 1) {
    for (const index of [currentIndex - offset, currentIndex + offset]) {
      const appId = applications[index]?.appId;
      if (isNullish(appId)) continue;
      const id = String(appId);
      if (id === current || id === 'null' || ids.includes(id)) continue;
      ids.push(id);
    }
  }
  return ids;
}

/**
 * Prefetch the main application payload for the prev/next neighbors of the
 * currently-open application, so navigating the slider hits warm cache.
 * Fire-and-forget; skips already-cached ids via `prefetchApplication`.
 */
export function prefetchAdjacentApplications(
  queryClient: QueryClient,
  applications: Array<{ appId?: string | number | null }> | undefined,
  currentAppId: string | number | null | undefined,
  radius = 1
): void {
  getAdjacentApplicationIds(applications, currentAppId, radius).forEach(
    (id) => {
      // Warm both the main payload (drives the shell) and the notes panel so the
      // whole default view is ready before the user lands on the neighbor.
      void prefetchApplication(queryClient, id);
      void prefetchApplicationNotes(queryClient, id);
    }
  );
}

/**
 * Evict cached application payloads + notes for apps OUTSIDE a `radius` window
 * around the current app. Bounds resident memory to O(2·radius) entries no
 * matter how far the user navigates — we no longer evict per-step (neighbors
 * stay warm for instant ‹ ›), so without this a long pipeline (e.g. an 852-app
 * list) would keep every visited/prefetched payload resident for the full 5-min
 * gcTime window. Only touches ids present in `applications`, so unrelated app
 * queries elsewhere in the app are left alone. Keep the window >= the prefetch
 * radius or you'd evict what you just warmed.
 */
export function evictApplicationsOutsideWindow(
  queryClient: QueryClient,
  applications: Array<{ appId?: string | number | null }> | undefined,
  currentAppId: string | number | null | undefined,
  radius = 2
): void {
  if (!applications?.length || isNullish(currentAppId)) return;
  const current = String(currentAppId);
  const currentIndex = applications.findIndex(
    (app) => !isNullish(app?.appId) && String(app.appId) === current
  );
  if (currentIndex === -1) return;

  const keep = new Set<string>();
  for (let offset = -radius; offset <= radius; offset += 1) {
    const appId = applications[currentIndex + offset]?.appId;
    if (!isNullish(appId)) keep.add(String(appId));
  }

  applications.forEach((app) => {
    if (isNullish(app?.appId)) return;
    const id = String(app.appId);
    if (id === 'null' || keep.has(id)) return;
    queryClient.removeQueries({ queryKey: getApplicationQueryKey(id) });
    // Prefix match so every notes page (load-more) for the app is dropped.
    queryClient.removeQueries({
      queryKey: [APPLICATION_NOTES_QUERY_ROOT, id],
    });
  });
}

/**
 * The `appId` `direction` steps away from `currentAppId` within `applications`
 * (`+1` = next, `-1` = prev). Returns undefined at the list boundaries or when
 * the current app isn't found.
 */
export function getNeighborAppId(
  applications: Array<{ appId?: string | number | null }> | undefined,
  currentAppId: string | number | null | undefined,
  direction: number
): string | undefined {
  if (!applications?.length || isNullish(currentAppId)) return undefined;
  const current = String(currentAppId);
  const currentIndex = applications.findIndex(
    (app) => !isNullish(app?.appId) && String(app.appId) === current
  );
  if (currentIndex === -1) return undefined;
  const neighbor = applications[currentIndex + direction]?.appId;
  return isNullish(neighbor) ? undefined : String(neighbor);
}

/**
 * Reactively report whether a neighbor application's payload has SETTLED — i.e.
 * the ‹ › arrow can move into it without landing on a cold spinner. Warms the
 * payload as a side effect. A nullish id (no neighbor) is treated as ready so
 * first/last gating stays owned by the button itself.
 *
 * Gate on settled (success OR error), not success alone: a neighbor that 403s
 * (closed job), 404s (deleted), 500s, or drops offline must NOT permanently
 * freeze the arrow. On error we let the user navigate into the destination —
 * which renders its own error / empty UI — instead of trapping them on a dead
 * chevron. (The app QueryClient sets retry:0 + refetchOnWindowFocus:false, so an
 * errored neighbor would otherwise never recover while the user stays put.)
 */
/**
 * Is the neighbor's payload in hand, so ‹ › can switch without a spinner?
 *
 * `active` defers the fetch: opening the drawer must not race the payload the
 * user is waiting on with both neighbors (browsers cap ~6 connections/host).
 * Pass false until the current application has settled.
 */
export function useApplicationReady(
  applicationId: string | number | null | undefined,
  active = true
): boolean {
  const id =
    isNullish(applicationId) || applicationId === ''
      ? null
      : String(applicationId);
  const hasNeighbor = !!id && id !== 'null';
  const { isSuccess, isError } = useQuery({
    queryKey: getApplicationQueryKey(id),
    queryFn: () =>
      API_SERVICE.getApplication(id as string, {
        include: ['stage_actions'],
      }),
    staleTime: APPLICATION_QUERY_STALE_MS,
    enabled: hasNeighbor && active,
  });
  return !hasNeighbor || isSuccess || isError;
}

function getResponseStatus(error: unknown): number {
  const err = error as { response?: { status?: number } };
  return err?.response?.status ?? 0;
}

function isServerError(error: unknown): boolean {
  const status = getResponseStatus(error);
  return status >= 500 && status < 600;
}

export const useApplicationHelper = () => {
  const context = useContext(CandidateContext) as {
    transferredApplicationOverride?: {
      applicationData: any;
      refetchApplication: () => void;
    } | null;
  };
  const applicationId = window.sessionStorage.getItem('candidate-app-id');
  const queryClient = useQueryClient();
  const toast = useShowToast();
  const hasShownErrorToast = useRef(false);

  useEffect(() => {
    hasShownErrorToast.current = false;
  }, [applicationId]);

  const queryKey = getApplicationQueryKey(applicationId);
  const {
    data: applicationDataFromQuery,
    isLoading,
    isFetching,
    isError,
    error,
  } = useQuery<ApplicationData>({
    queryKey,
    queryFn: () =>
      API_SERVICE.getApplication(applicationId as string, {
        include: ['stage_actions'],
      }),
    staleTime: APPLICATION_QUERY_STALE_MS,
    enabled:
      !context?.transferredApplicationOverride &&
      applicationId !== 'null' &&
      !!applicationId &&
      queryKey[0] !== 'application/__none__',
    retry: (failureCount, err) =>
      isServerError(err) ? false : failureCount < 3,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (
      isError &&
      error &&
      isServerError(error) &&
      !hasShownErrorToast.current
    ) {
      hasShownErrorToast.current = true;
      toast({ title: SUPPORT_ERROR_MESSAGE, status: 'error' });
    }
  }, [isError, error, toast]);

  const refetchApplication = async () => {
    if (context?.transferredApplicationOverride) {
      context.transferredApplicationOverride.refetchApplication();
      return;
    }
    if (!applicationId) return;
    hasShownErrorToast.current = false;
    await queryClient.invalidateQueries({ queryKey });
  };

  const clearPreviousApplicationCache = (previousID: string) => {
    if (!previousID) return;
    hasShownErrorToast.current = false;
    queryClient.removeQueries({
      queryKey: getApplicationQueryKey(String(previousID)),
    });
  };

  if (context?.transferredApplicationOverride) {
    return {
      applicationData: {
        ...context.transferredApplicationOverride.applicationData,
        transferred: true,
      },
      isLoading: false,
      isFetching: false,
      refetchApplication,
      clearPreviousApplicationCache,
    };
  }

  return {
    applicationData: applicationDataFromQuery,
    isLoading,
    isFetching,
    isError,
    isServerError: isError && isServerError(error),
    refetchApplication,
    clearPreviousApplicationCache,
  };
};
